import Accordion from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  UpgradeLabel,
  useLockedAction,
} from "@components/plan-subscription-lock";
import type { StoryType } from "@config";
import { cn } from "@lib/cn";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import {
  FolderCreateDialog,
  SidebarSavedTab,
} from "@modules/slides-library/features/folders";
import {
  ImportFromStoriesContent,
  ImportFromStoriesSidebar,
} from "@modules/slides-library/features/import-from-stories";
import {
  ArchivedFoldersTree,
  RecentlyDeletedView,
} from "@modules/slides-library/features/recently-deleted";
import { SavedSlidesView } from "@modules/slides-library/features/saved-slides";
import { CategoryItem } from "@modules/slides-library/features/template-slides";
import { TemplateCategorySidebarGroups } from "@modules/slides-library/features/template-slides/components/template-category-groups";
import {
  useGetAiSlides,
  useScrollToCategory,
} from "@modules/slides-library/features/template-slides/hooks";
import { store, useAppDispatch } from "@store";
import { reset as resetAiSlide } from "@store/ai-slides";
import { closeDialog, openDialog } from "@store/dialogs";
import {
  IconChevronRight,
  IconClock,
  IconFiles,
  IconFlame,
  IconLayoutBoard,
  IconPlus,
  IconStar,
  IconTools,
  IconTrash,
  IconUser,
  IconUsersGroup,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import {
  type ComponentType,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DEFAULT_DESIGN_SYSTEM } from "@/core/constants";
import { useAccountStore } from "@/features/account-management";
import { useCreatorLiteSourceRules } from "@/features/standalone-versions";
import type { CreateTarget } from "@/features/standalone-versions";
import { handleDesign } from "@/shared";
import { useGetThemeByIdQuery } from "@/shared/services";
import type { TMasterSlide } from "@/shared/types";
import { FolderBreadcrumbs } from "./folder-breadcrumbs";
import { SlidesLibrarySidebarSkeleton } from "./skeleton/slides-library-sidebar-skeleton";
import { SlideLibraryDialogShell } from "./slide-library-dialog-shell";
import {
  isMostPopularTitle,
  SidebarIconItem,
  SlideGridContent,
} from "./slide-library-shared";
import { type SelectedSlide, useSlidesLibraryStoreShallow } from "../store";

const SLIDES_LIBRARY_DIALOG_ID = "slides-library";

type UnifiedTab = "saved" | "layouts" | "templates" | "import-from-stories";
type SavedSection = "user-slides" | "team-slides" | "recently-deleted";

const TOP_TABS: {
  value: UnifiedTab;
  label: string;
  icon: TablerIcon;
}[] = [
  { value: "saved", label: "Saved slides", icon: IconStar },
  { value: "layouts", label: "Layouts", icon: IconLayoutBoard },
  { value: "templates", label: "Storydoc's templates", icon: IconTools },
  {
    value: "import-from-stories",
    label: "Import from stories",
    icon: IconFiles,
  },
];

const SAVED_SECTIONS: {
  value: SavedSection;
  label: string;
  icon: ComponentType<{ className?: string; stroke?: number }>;
  hasPlus: boolean;
}[] = [
  { value: "user-slides", label: "My slides", icon: IconUser, hasPlus: true },
  {
    value: "team-slides",
    label: "Team slides",
    icon: IconUsersGroup,
    hasPlus: true,
  },
  {
    value: "recently-deleted",
    label: "Recently deleted",
    icon: IconTrash,
    hasPlus: false,
  },
];

type Props = {
  view?: "library" | "editor";
  initialTab?: UnifiedTab;
  initialSavedSection?: SavedSection;
  onSuccess?: (story: StoryType) => void;
  /**
   * Open as a PICKER: the selected slides go to this callback and the dialog
   * closes, instead of a story being created or slides being added to the
   * open one. See SlidesLibraryFloatingActions.
   */
  onPickSlides?: (slides: SelectedSlide[]) => void;
  /**
   * Library view: what the picked slides become -- a story (default) or a
   * standalone version. See features/standalone-versions.
   */
  createTarget?: CreateTarget;
};

const SlidesLibraryDialogInner = ({
  view = "library",
  initialTab = "saved",
  initialSavedSection = "user-slides",
  onSuccess,
  onPickSlides,
  createTarget = "story",
}: Props) => {
  const dispatch = useAppDispatch();
  const {
    setType,
    setTemplateTab,
    setView,
    setOnSuccess,
    setOnPickSlides,
    setCreateTarget,
    reset,
    folderId,
    isInSearch,
  } = useSlidesLibraryStoreShallow((s) => ({
    setType: s.setType,
    setTemplateTab: s.setTemplateTab,
    setView: s.setView,
    setOnSuccess: s.setOnSuccess,
    setOnPickSlides: s.setOnPickSlides,
    setCreateTarget: s.setCreateTarget,
    reset: s.reset,
    folderId: s.folderId,
    isInSearch: !!s.search,
  }));
  const { isLocked: isTeamLocked, onLockedAction } = useLockedAction({
    feature: "save-as-team-slide",
  });

  // Theme the dialog container so slide previews resolve `var(--theme-color-*)`.
  // In editor view we intentionally DON'T apply a theme here: the container then
  // inherits the open story's design system from `document.documentElement` (set
  // by `use-get-story.tsx`), so previews match the canvas — and the chart
  // colors, which resolve against the document — instead of the account default.
  // Library view has no story theme on the root, so there we apply the account's
  // default theme (falling back to DEFAULT_DESIGN_SYSTEM) to keep previews
  // readable.
  const containerRef = useRef<HTMLDivElement>(null);
  const accountDsId = useAccountStore((s) => s.account?.dsId);
  const { data: designSystem, isLoading: isDesignSystemLoading } =
    useGetThemeByIdQuery(accountDsId as string);
  useEffect(() => {
    if (view === "editor") return;
    if (isDesignSystemLoading) return;
    handleDesign(
      (designSystem?.designSystem as Record<string, string>) ??
        DEFAULT_DESIGN_SYSTEM,
      containerRef.current,
    );
  }, [view, designSystem, isDesignSystemLoading]);

  // A Creator lite works from the team's slides and the org's documents and
  // versions: Storydoc's templates and layouts are not offered to them, nor
  // slides of their own, so the library opens on Team slides for them.
  const isCreatorLite = useCreatorLiteSourceRules();
  const hiddenTabs: UnifiedTab[] = isCreatorLite
    ? ["templates", "layouts"]
    : [];
  const [activeTab, setActiveTab] = useState<UnifiedTab>(
    hiddenTabs.includes(initialTab) ? "saved" : initialTab,
  );
  const startSavedSection: SavedSection = isCreatorLite
    ? "team-slides"
    : initialSavedSection;
  const [activeSavedSection, setActiveSavedSection] =
    useState<SavedSection>(startSavedSection);
  const [expandedSavedSections, setExpandedSavedSections] = useState<string[]>([
    startSavedSection,
  ]);

  // The picker callback and the create target can change while the dialog
  // stays open, so they are written on their own. They must NOT sit in the
  // setup effect below: its cleanup resets the whole store, and a caller
  // passing an inline callback would re-run that cleanup on every one of its
  // renders -- wiping the selected slides, the active type, the search and
  // the import-from-stories navigation out from under an open dialog.
  useEffect(() => {
    setOnPickSlides(onPickSlides);
  }, [setOnPickSlides, onPickSlides]);

  useEffect(() => {
    setCreateTarget(createTarget);
  }, [setCreateTarget, createTarget]);

  // One-shot setup: view + onSuccess live for the dialog's lifetime.
  // Tab-driven type/templateTab live in the effect below.
  useEffect(() => {
    setView(view);
    setOnSuccess(onSuccess);
    return () => {
      reset();
      // Match the deleted SlidesLibrary's cleanup: clear leftover AI-slide
      // state so it doesn't bleed into the next dialog session.
      //
      // NOT WHILE A BUILD IS RUNNING. "Add with AI" in here closes the dialog
      // and starts the build in the same tick, so this cleanup fired 83ms
      // after the status was set to "loading" and reset it to idle — while
      // the build carried on for another eight seconds. The overlay watches
      // that status, so the "Adding AI slide… [Stop]" banner flashed for one
      // frame and then there was no sign anything was happening at all.
      // Measured on this route, not inferred.
      //
      // A status of "loading" (or an "error" the overlay is still offering a
      // retry for) means the state belongs to work in flight, not to the
      // dialog session that is ending. Read live rather than through a
      // selector: what matters is the value at unmount.
      const aiStatus = store.getState().aiSlides.status;
      if (aiStatus !== "loading" && aiStatus !== "error") {
        dispatch(resetAiSlide());
      }
    };
    // Mount only: the cleanup above tears the dialog's whole session down, so
    // nothing that changes while it is open may appear here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // setType/setTemplateTab is owned by the parent so sibling-tab content
  // can't race each other the way the per-type dialogs would if mounted
  // together.
  useEffect(() => {
    if (activeTab === "saved") {
      setType(activeSavedSection);
      return;
    }
    if (activeTab === "layouts") {
      setType("layouts");
      return;
    }
    setType("templates");
    setTemplateTab(
      activeTab === "import-from-stories"
        ? "import-from-stories"
        : "single-slides",
    );
  }, [activeTab, activeSavedSection, setType, setTemplateTab]);

  return (
    <SlideLibraryDialogShell
      dialogId={SLIDES_LIBRARY_DIALOG_ID}
      title="Slides library"
      containerRef={containerRef}
      headerExtra={
        <TabStrip
          activeTab={activeTab}
          onChange={setActiveTab}
          hidden={hiddenTabs}
        />
      }
      sidebar={
        <DialogSidebar
          activeTab={activeTab}
          expandedSavedSections={expandedSavedSections}
          isTeamLocked={!!isTeamLocked}
          onTeamLockedAction={onLockedAction}
          folderId={folderId}
          isInSearch={isInSearch}
          onSavedSectionChange={setActiveSavedSection}
          onExpandedSavedSectionsChange={setExpandedSavedSections}
          onAddFolder={(section) =>
            dispatch(
              openDialog({
                id: FolderCreateDialog.id,
                content: FolderCreateDialog,
                props: { activeFolderId: section },
              }),
            )
          }
        />
      }
      scrollKey={
        activeTab === "saved" ? `saved:${activeSavedSection}` : activeTab
      }
      onClose={() => dispatch(closeDialog(SLIDES_LIBRARY_DIALOG_ID))}
    >
      <DialogContent
        activeTab={activeTab}
        activeSavedSection={activeSavedSection}
      />
    </SlideLibraryDialogShell>
  );
};

export const SlidesLibraryDialog = Object.assign(SlidesLibraryDialogInner, {
  id: SLIDES_LIBRARY_DIALOG_ID,
});

// ---------------------------------------------------------------------------
// Tab strip (Figma: tw-h-8, gap-2, active tw-bg-[#f3f2f5], inactive opacity-50)
// ---------------------------------------------------------------------------

const TabStrip = ({
  activeTab,
  onChange,
  hidden = [],
}: {
  activeTab: UnifiedTab;
  onChange: (tab: UnifiedTab) => void;
  /** Creator lite: Storydoc's templates and layouts are not theirs to start from. */
  hidden?: UnifiedTab[];
}) => (
  <div className="tw-flex tw-items-center tw-gap-2 tw-pb-2 tw-pl-6 tw-pr-4">
    {TOP_TABS.filter((tab) => !hidden.includes(tab.value)).map((tab) => {
      const TabIcon = tab.icon;
      const isActive = activeTab === tab.value;
      return (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={cn(
            "tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded-lg tw-border-none tw-px-3 tw-font-sans tw-text-xs tw-font-medium tw-text-blue tw-outline-none tw-transition-all",
            isActive ? "tw-bg-[#f3f2f5]" : "tw-bg-transparent tw-opacity-50",
          )}
        >
          <TabIcon size={14} stroke={1.5} className="tw-shrink-0" />
          {tab.label}
        </button>
      );
    })}
  </div>
);

// ---------------------------------------------------------------------------
// Sidebar — branches per tab
// ---------------------------------------------------------------------------

const DialogSidebar = ({
  activeTab,
  expandedSavedSections,
  isTeamLocked,
  onTeamLockedAction,
  folderId,
  isInSearch,
  onSavedSectionChange,
  onExpandedSavedSectionsChange,
  onAddFolder,
}: {
  activeTab: UnifiedTab;
  expandedSavedSections: string[];
  isTeamLocked: boolean;
  onTeamLockedAction: () => void;
  folderId: string;
  isInSearch: boolean;
  onSavedSectionChange: (section: SavedSection) => void;
  onExpandedSavedSectionsChange: (sections: string[]) => void;
  onAddFolder: (section: SavedSection) => void;
}) => {
  if (activeTab === "saved") {
    return (
      <SavedSidebar
        expandedSections={expandedSavedSections}
        isTeamLocked={isTeamLocked}
        onTeamLockedAction={onTeamLockedAction}
        folderId={folderId}
        onSectionChange={onSavedSectionChange}
        onExpandedChange={onExpandedSavedSectionsChange}
        onAddFolder={onAddFolder}
      />
    );
  }
  if (activeTab === "import-from-stories") {
    return <ImportFromStoriesSidebar />;
  }
  return (
    <CategorySidebar
      mode={activeTab === "layouts" ? "layouts" : "templates"}
      isInSearch={isInSearch}
    />
  );
};

// ---------------------------------------------------------------------------
// Saved sidebar (Accordion: My slides / Team slides / Recently deleted)
// Mirrors SavedSlidesDialog's sidebar; lifted out for clarity.
// ---------------------------------------------------------------------------

const SavedSidebar = ({
  expandedSections,
  isTeamLocked,
  onTeamLockedAction,
  folderId,
  onSectionChange,
  onExpandedChange,
  onAddFolder,
}: {
  expandedSections: string[];
  isTeamLocked: boolean;
  onTeamLockedAction: () => void;
  folderId: string;
  onSectionChange: (section: SavedSection) => void;
  onExpandedChange: (sections: string[]) => void;
  onAddFolder: (section: SavedSection) => void;
}) => {
  // A Creator lite has no slides of their own here: only the team's.
  const isCreatorLite = useCreatorLiteSourceRules();
  const sections = isCreatorLite
    ? SAVED_SECTIONS.filter((section) => section.value === "team-slides")
    : SAVED_SECTIONS;
  return (
    <Accordion
      type="multiple"
      value={expandedSections}
      onValueChange={onExpandedChange}
      className="tw-space-y-4 tw-py-4"
    >
      {sections.map((section) => {
        const SectionIcon = section.icon;
        const isExpanded = expandedSections.includes(section.value);
        const isActive = folderId === section.value;
        const teamLocked = section.value === "team-slides" && isTeamLocked;
        return (
          <Accordion.Item
            key={section.value}
            value={section.value}
            className="tw-px-3"
          >
            <AccordionPrimitive.Header
              className={cn(
                "tw-group tw-m-0 tw-flex tw-h-8 tw-items-center tw-rounded-md tw-font-sans tw-transition-colors",
                isActive ? "tw-bg-blue-100" : "hover:tw-bg-blue-100",
              )}
            >
              <div
                onClick={() => {
                  if (teamLocked) return onTeamLockedAction();
                  onSectionChange(section.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (teamLocked) return onTeamLockedAction();
                    onSectionChange(section.value);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={section.label}
                aria-pressed={isActive}
                className={cn(
                  "tw-flex tw-flex-1 tw-cursor-pointer tw-items-center tw-gap-2 tw-pl-2 tw-text-sm tw-font-normal tw-text-blue focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200",
                  teamLocked && "tw-opacity-50",
                )}
              >
                <SectionIcon
                  className="tw-size-4 tw-shrink-0 tw-text-blue"
                  stroke={1}
                />
                <span>{section.label}</span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-0.5 tw-pr-1">
                {teamLocked ? (
                  <UpgradeLabel feature="save-as-team-slide" />
                ) : (
                  <>
                    {section.hasPlus && (
                      <button
                        type="button"
                        onClick={() => onAddFolder(section.value)}
                        className={cn(
                          "tw-bg-transparent tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-p-0 tw-outline-none hover:tw-bg-blue-100",
                          isActive
                            ? "tw-visible"
                            : "tw-invisible focus:tw-visible group-focus-within:tw-visible group-hover:tw-visible",
                        )}
                      >
                        <IconPlus
                          className="tw-size-3.5 tw-text-blue tw-opacity-50"
                          stroke={1.5}
                        />
                      </button>
                    )}
                    {section.value !== "recently-deleted" && (
                      <AccordionPrimitive.Trigger className="tw-bg-transparent tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-none tw-p-0 tw-outline-none hover:tw-bg-blue-100">
                        <IconChevronRight
                          className={cn(
                            "tw-size-3.5 tw-text-blue tw-opacity-50 tw-transition-transform",
                            isExpanded && "tw-rotate-90",
                          )}
                          stroke={1.5}
                        />
                      </AccordionPrimitive.Trigger>
                    )}
                  </>
                )}
              </div>
            </AccordionPrimitive.Header>
            <Accordion.Content>
              {section.value === "user-slides" && (
                <SidebarSavedTab type="user-slides" showHeader={false} />
              )}
              {section.value === "team-slides" && (
                <SidebarSavedTab type="team-slides" showHeader={false} />
              )}
              {section.value === "recently-deleted" && <ArchivedFoldersTree />}
            </Accordion.Content>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};

// ---------------------------------------------------------------------------
// Category sidebar (Layouts + Templates share the same shape; data differs)
// ---------------------------------------------------------------------------

const CategorySidebar = ({
  mode,
  isInSearch,
}: {
  mode: "layouts" | "templates";
  isInSearch: boolean;
}) => {
  const { data, isLoading } = useGetAiSlides();
  const scrollToCategory = useScrollToCategory();
  const onCategoryClick = useCallback(
    (id: string) => scrollToCategory(id),
    [scrollToCategory],
  );

  const slidesByCategory = (data?.slides ?? {}) as Record<string, unknown[]>;
  const hasSlides = (id: string) => (slidesByCategory[id]?.length ?? 0) > 0;
  // The Most popular bucket is keyed by its title, not a category id.
  const hasMostPopular = Object.entries(slidesByCategory).some(
    ([key, list]) => isMostPopularTitle(key) && (list?.length ?? 0) > 0,
  );

  const body = (
    <div className="tw-flex tw-flex-col tw-gap-1 tw-px-2 tw-py-4">
      {!isInSearch && (
        <>
          <SidebarIconItem
            id="recently-used"
            icon={IconClock}
            label="Recently used"
            onClick={onCategoryClick}
          />
          {(mode !== "layouts" || hasMostPopular) && (
            <SidebarIconItem
              id="most-popular"
              icon={IconFlame}
              label="Most popular"
              onClick={onCategoryClick}
            />
          )}
          <div className="tw-h-3" />
        </>
      )}
      {isLoading ? (
        <SlidesLibrarySidebarSkeleton />
      ) : mode === "layouts" ? (
        data?.categories
          ?.filter(
            (item: TMasterSlide) =>
              !isMostPopularTitle(item.title) && hasSlides(item._id),
          )
          .map((item: TMasterSlide) => (
            <CategoryItem
              key={item._id}
              {...item}
              onClick={onCategoryClick}
              isInSearch={isInSearch}
              className="tw-px-2"
            />
          ))
      ) : (
        <TemplateCategorySidebarGroups
          categories={data?.categories ?? []}
          onClick={onCategoryClick}
          isInSearch={isInSearch}
        />
      )}
    </div>
  );

  // Templates sidebar can get long enough to need scroll; Layouts doesn't,
  // but a scroll wrapper is harmless either way.
  return <ScrollArea className="tw-h-full">{body}</ScrollArea>;
};

// ---------------------------------------------------------------------------
// Content — branches per tab
// ---------------------------------------------------------------------------

const DialogContent = ({
  activeTab,
  activeSavedSection,
}: {
  activeTab: UnifiedTab;
  activeSavedSection: SavedSection;
}) => {
  if (activeTab === "saved") {
    if (activeSavedSection === "recently-deleted") {
      return <RecentlyDeletedView />;
    }
    return (
      <>
        <FolderBreadcrumbs />
        <SavedSlidesView />
      </>
    );
  }
  if (activeTab === "import-from-stories") {
    return <ImportFromStoriesContent />;
  }
  return <SlideGridContent />;
};
